<template>
  <div class="forgot-page-shell">
    <main class="forgot-page-main">
      <div class="forgot-page-card">
        <BrandLogo class="forgot-page-logo" />
        <h1 class="forgot-page-heading">{{ sent ? $t('forgot.sentTitle') : $t('forgot.title') }}</h1>

        <!-- Siempre la misma respuesta, exista o no la cuenta: no se enumera. -->
        <p v-if="sent" class="forgot-page-lede" role="status" data-test="forgot-sent">
          {{ $t('forgot.sentBody', { email: sentTo }) }}
        </p>

        <template v-else>
          <p class="forgot-page-lede">{{ $t('forgot.hint') }}</p>

          <v-form ref="form" v-model="valid" @submit.prevent="handleSubmit">
            <v-text-field
              v-model="email"
              type="email"
              :label="$t('forgot.email')"
              outlined
              dense
              persistent-placeholder
              autocomplete="email"
              :rules="emailRules"
              :disabled="loading"
              class="forgot-page-input"
              hide-details="auto"
              data-test="forgot-email"
            />

            <TurnstileWidget ref="turnstile" @token="captchaToken = $event" />

            <p v-if="errorMsg" class="forgot-page-error" role="alert">{{ errorMsg }}</p>

            <v-btn
              type="submit"
              block
              depressed
              large
              :loading="loading"
              :disabled="!valid || loading || esperandoCaptcha"
              class="forgot-page-submit"
            >
              {{ $t('forgot.submit') }}
            </v-btn>
          </v-form>
        </template>

        <p class="forgot-page-footer">
          <router-link to="/signin" class="forgot-page-link">{{ $t('forgot.backToSignIn') }}</router-link>
        </p>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { api } from '@/services/api';
import { estadoHttp, segundosDeEspera, tipoDeNegativa } from '@/services/apiErrors';
import { turnstileEncendido } from '@/services/turnstile';
import BrandLogo from '@/components/brand/BrandLogo.vue';
import TurnstileWidget from '@/components/TurnstileWidget.vue';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * «Olvidé la contraseña» pasa por el backend (`POST /api/auth/recuperar`), que
 * limita las peticiones y contesta 202 siempre. La pantalla hace lo mismo:
 * enseña el MISMO mensaje neutro pase lo que pase, salvo dos casos en los que la
 * persona tiene que hacer algo y que no dicen nada de la cuenta —demasiadas
 * peticiones (429) y un captcha que no vale (400 CAPTCHA)—.
 */
export default defineComponent({
  name: 'ForgotPasswordView',
  components: { BrandLogo, TurnstileWidget },
  data() {
    return {
      valid: false,
      email: '',
      sentTo: '',
      loading: false,
      sent: false,
      errorMsg: '',
      captchaToken: null as string | null,
      emailRules: [
        (v: string): boolean | string => !!v || String(this.$t('forgot.emailRequired')),
        (v: string): boolean | string => EMAIL_RE.test(v) || String(this.$t('forgot.emailInvalid')),
      ],
    };
  },
  computed: {
    esperandoCaptcha(): boolean {
      return turnstileEncendido && !this.captchaToken;
    },
  },
  methods: {
    async handleSubmit(): Promise<void> {
      if (!this.valid || this.esperandoCaptcha) return;
      this.loading = true;
      this.errorMsg = '';
      const email = this.email.trim().toLowerCase();
      try {
        await api.post('/api/auth/recuperar', {
          email,
          ...(this.captchaToken ? { captcha_token: this.captchaToken } : {}),
        });
        this.mostrarEnviado(email);
      } catch (err: unknown) {
        if (estadoHttp(err) === 429) {
          const segundos = segundosDeEspera(err);
          this.errorMsg = segundos
            ? String(this.$t('forgot.errors.tooManySeconds', { seconds: segundos }))
            : String(this.$t('forgot.errors.tooMany'));
        } else if (tipoDeNegativa(err) === 'CAPTCHA') {
          this.errorMsg = String(this.$t('forgot.errors.captcha'));
        } else {
          this.mostrarEnviado(email);
        }
      } finally {
        this.loading = false;
        // El token de Turnstile ya se gastó: el siguiente intento necesita otro.
        (this.$refs.turnstile as unknown as { reset?: () => void } | undefined)?.reset?.();
      }
    },
    mostrarEnviado(email: string): void {
      this.sentTo = email;
      this.sent = true;
    },
  },
});
</script>

<style scoped>
.forgot-page-shell {
  --ink:        var(--ii-ink);
  --ink-hushed: var(--ii-ink-2);
  --border:     var(--ii-rule);
  min-height: 100vh;
  background: var(--casa-fondo);
  color: var(--ink);
  font-family: var(--ii-display);
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
}

.forgot-page-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--casa-espacio-8) var(--casa-espacio-6) 64px;
}

.forgot-page-card {
  width: 100%;
  max-width: 460px;
}

.forgot-page-logo { margin-bottom: var(--casa-espacio-2); }

.forgot-page-heading {
  margin: 0 0 var(--casa-espacio-3);
  font-family: inherit;
  font-size: 40px;
  line-height: 1.0;
  font-weight: 500;
  letter-spacing: -0.04em;
  color: var(--ink);
  text-wrap: balance;
}
@media (min-width: 768px) {
  .forgot-page-heading { font-size: 48px; }
}

.forgot-page-lede {
  margin: 0 0 28px;
  font-family: inherit;
  font-size: var(--casa-letra-seccion);
  font-weight: 400;
  line-height: 1.5;
  color: var(--ink-hushed);
  word-break: break-word;
  text-wrap: balance;
}

.forgot-page-input { margin-bottom: var(--casa-espacio-3); }

.forgot-page-error {
  margin: 0 0 var(--casa-espacio-3);
  padding: var(--casa-espacio-2-5) var(--casa-espacio-3);
  background: var(--casa-fondo);
  border: 1px solid var(--casa-error);
  border-left: 3px solid var(--casa-error);
  border-radius: var(--casa-radio-tarjeta);
  color: var(--casa-error);
  font-size: var(--casa-letra-control);
  font-weight: 400;
}

.forgot-page-submit {
  margin-top: var(--casa-espacio-1);
  height: 46px !important;
  background-color: var(--ii-accent) !important;
  color: var(--casa-texto-inverso) !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  border-radius: var(--casa-radio-tarjeta) !important;
  box-shadow: none !important;
}
.forgot-page-submit:hover { background-color: var(--ii-accent-2) !important; }
.forgot-page-submit:disabled {
  background-color: var(--casa-fondo-app) !important;
  color: rgba(12,10,8,0.4) !important;
}

.forgot-page-footer {
  margin-top: var(--casa-espacio-8);
  font-family: inherit;
  font-size: var(--casa-letra-texto);
  font-weight: 400;
  color: var(--ink-hushed);
  line-height: 1.5;
}
.forgot-page-link {
  color: var(--ink);
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 400;
}

.forgot-page-input :deep(.v-input__slot) {
  min-height: 46px !important;
  border-radius: var(--casa-radio-tarjeta) !important;
}
.forgot-page-input :deep(fieldset) {
  border-color: var(--border) !important;
  border-width: 1px !important;
}
.forgot-page-input :deep(.v-text-field--outlined.v-input--is-focused fieldset) {
  border-color: var(--ii-accent) !important;
  border-width: 1px !important;
}
.forgot-page-input :deep(.v-label) {
  color: var(--ink-hushed) !important;
  font-family: var(--ii-display) !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
}
.forgot-page-input :deep(input) {
  font-family: var(--ii-display) !important;
  font-weight: 400 !important;
  font-size: var(--casa-letra-cuerpo) !important;
  padding: var(--casa-espacio-2-5) var(--casa-espacio-1) !important;
  color: var(--ink) !important;
}

@media (max-width: 480px) {
  .forgot-page-heading { font-size: 36px; }
  .forgot-page-main { padding: var(--casa-espacio-6) var(--casa-espacio-5); }
}
</style>
